import {
  Fab,
  IconButton,
  Modal,
  TableCell,
  withStyles,
} from '@material-ui/core';
import {
  Map as MapIcon,
  AccessTime,
} from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import EditHistoryModal from '~/components/EditHistoryModal';
import PF2MDataTable from '~/components/PF2MDataTable';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import {
  createTransportItem,
  deleteTransportItem,
  restoreOriginalTransport,
  searchTransportList,
  setTransportValues,
  updateTransportList,
} from '~/store/dailyparts/actions';
import {
  formatDate,
  formatTime,
  newMomentDate,
  parseDate,
  parseDateToTimestamp,
  subtractDaysFromNow,
} from '~/utils/moment';
import styles from '../styles';
import RenderDistanceManagerField from './RenderDistanceManagerField';
import TransportCycleTimesModal from './TransportCycleTimesModal';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';
import { resolveHandlingTypeId } from '~/utils/handlingType';


const ESCAVATOR_ID = 1;
const LOADER_ID = 15;
const ROCK_FILL_ID = 29;

const TransportList = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const filters = useSelector(state => state.dailyParts.filters);
  const userAccessLevel = useSelector(state => state.auth.user.level);

  const transportList = useSelector(state => state.dailyParts.transportList);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const equipments = useSelector(state => state.manager.equipments);

  const operatorsGroups = useSelector(state => state.manager.operatorsGroups
    .filter(o => o.name), shallowEqual);
  const operators = useSelector(state => state.manager.operators.filter(o => o.name), shallowEqual);

  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const sleepLevels = useSelector(state => state.manager.sleepLevels);
  const transportOperationTypes = useSelector(state => state.manager.transportOperationTypes);
  const ogList = useSelector(state => state.dailyParts.originalTransportList);
  const handlingTypes = useSelector(state => state.manager.handlingTypes);
  const flowMovements = useSelector(state => state.manager.flowMovements);

  const [selectedTransportReport, setSelectedTransportReport] = useState();
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [isReportUrlModalOpen, setIsReportUrlModalOpen] = useState(false);

  const { value: editLoadDefaultValue } = usePlatformConfiguration('edit_load_default');
  const clientReportUrl = usePlatformConfiguration('url_reports_maps');

  const editLoadDefault = parseInt(editLoadDefaultValue) === 1 ? 'always' : 'never';
  const loadDefault = useSelector(state => state.manager.loads);

  const closures = useSelector(state => state.manager.closures);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const { value: autoFillMovementType } = usePlatformConfiguration('auto_fill_movement_type');

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);
  const transportPagination = useSelector(
    state => state.dailyParts.pagination.transportPagination,
  );


  const filterLoadDefault = (data, idMaterial, idTruck) => data.find(ld => (
    ld.id_material === idMaterial && ld.id_equip === idTruck
  ));

  const checkLoadBalance = (data) => {
    if (parseInt(editLoadDefaultValue) === 1) return data;
    const oldData = data;
    if (oldData.id_group !== 0
      && oldData.id_truck !== 0
      && oldData.id_material_truck !== 0) {
      const LoadBalanceValue = filterLoadDefault(loadDefault,
        oldData.id_material_truck,
        oldData.id_truck);
      oldData.load_manager = LoadBalanceValue?.value || 0;
    }
    return oldData;
  };

  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);

  const validateDateOrTimeField = key => (rowData) => {
    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || formatDate(currentDatetime);
    const stTime = rowData.start_time || formatTime(currentDatetime);
    const endDate = rowData.end_date || formatDate(currentDatetime);
    const endTime = rowData.end_time || formatTime(currentDatetime);

    const startDateTimestamp = parseDateToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateToTimestamp(endDate, endTime);

    const isEnableClosureOn = enableClosure && closures?.length > 0;
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure;
    }

    const foundField = rowData[key];
    const invalidMsg = {
      isValid: false, helperText: translate('validation:RequiredField'),
    };

    const invalidEditPeriodDateMsg = {
      isValid: false, helperText: translate('validation:InvalidPeriodDate'),
    };

    const defaultValidation = !foundField || foundField.includes('Invalid');
    const validateTime = defaultValidation || foundField.includes('_');
    const validateDate = defaultValidation || foundField.length === 0;

    if (key === 'end_date' && startDateTimestamp > endDateTimestamp) {
      return { isValid: false, helperText: translate('validation:FinalDateSmallerThanInitialDate') };
    }

    if (key.includes('time') && validateTime) return invalidMsg;
    if (key.includes('date') && validateDate) return invalidMsg;
    if (key === 'start_date' && parseDate(rowData[key], true).isBefore(userLimitEdit)) return invalidEditPeriodDateMsg;

    return { isValid: true };
  };

  const validateLookupField = key => (rowData) => {
    if (['', '0'].includes(String(rowData[key]))) {
      return { isValid: false, helperText: translate('validation: RequiredField') };
    }
    return { isValid: true };
  };

  const useCreateMemoMapById = (array, key) => useMemo(
    () => Object.fromEntries((array ?? []).map(item => [item[key], item])),
    [array, key],
  );

  const subElementsById = useCreateMemoMapById(subElements, 'id');
  const materialsById = useCreateMemoMapById(materials, 'id');

  const getAutoHandlingTypeId = row => resolveHandlingTypeId({
    row,
    subElementsById,
    materialsById,
    flowMovements,
  });

  const movementTypeFilter = (option, row) => (
    String(option.id) === getAutoHandlingTypeId(row)
  );

  const calculateDMT = ({ full_distance: fd, empty_distance: ed }) => (fd + ed) / 2;

  const redirectToReport = (row) => {
    const startTimestamp = parseDateToTimestamp(row.start_date, row.start_time);
    const endTimestamp = parseDateToTimestamp(row.end_date, row.end_time);
    const equipId = row.id_truck;

    const paramsObj = {
      data_inicial: startTimestamp,
      data_final: endTimestamp,
      equip_ids: equipId,
    };

    const searchParams = new URLSearchParams(paramsObj);

    if (clientReportUrl.value === '') {
      setIsReportUrlModalOpen(true);
      return;
    }

    const reportUrl = new URL(clientReportUrl.value);

    reportUrl.search = searchParams;

    window.open(reportUrl, '_blank');
  };

  const renderRouteHistory = (row) => {
    const renderExceptionTypes = [14, 15];
    const renderReportButton = renderExceptionTypes.includes(row.exception_type);
    const style = {
      backgroundColor: renderReportButton ? 'rgb(242, 170, 0)' : 'lightgray',
      color: 'white',
    };

    return (
      <div style={{ justifySelf: 'center' }}>
        <TableCell width="50px">
          <Fab
            size="small"
            style={style}
            disabled={!renderReportButton}
            onClick={() => redirectToReport(row)}
          >
            <MapIcon />
          </Fab>
        </TableCell>
      </div>
    );
  };

  // 1. Para a definição de campos obrigatórios com mensagem de erro,
  //    a chave "required" é necessária.
  // 2. Se o campo for de busca e estiver com o "lookup" e/ou "lookupFilter"
  //    se torna necessário a chave "customType" sendo "filterLookup".
  const columns = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:RouteHistory'),
      render: renderRouteHistory,
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      required: true,
      validate: validateDateOrTimeField('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      required: true,
      validate: validateDateOrTimeField('end_date'),
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      required: true,
      validate: validateDateOrTimeField('start_time'),
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      required: true,
      validate: validateDateOrTimeField('end_time'),
    },
    {
      editable: 'never',
      title: translate('common:EquipmentType'),
      field: 'id_equip',
      lookup: equipmentTypes,
      customType: 'filterLookup',
      required: true,
      validate: validateLookupField('id_equip'),
      customOnChange: () => ({ id_group: '0', id_truck: '0' }),
      tooltip: true,
    },
    {
      editable: 'always',
      customType: 'filterLookup',
      title: translate('common:EquipmentGroup'),
      field: 'id_group',
      validate: validateLookupField('id_group'),
      required: true,
      lookup: equipmentsGroups,
      lookupFilter: (e, f) => String(e.id_equipament) === String(f.id_equip)
        || String(f.id_equip) === '0',
      customOnChange: () => ({ id_truck: '0' }),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      customType: 'filterLookup',
      validate: validateLookupField('id_truck'),
      field: 'id_truck',
      required: true,
      lookup: equipments,
      lookupFilter: (e, f) => String(e.id_group) === String(f.id_group)
        || String(f.id_group) === '0',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      validate: validateLookupField('id_mine'),
      required: true,
      field: 'id_mine',
      lookup: elements,
      customOnChange: () => ({ id_mine_origin: '0' }),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('id_mine_origin'),
      lookup: subElements,
      lookupFilter: (e, f) => String(e.id_element) === String(f.id_mine)
        || String(f.id_mine) === '0',
      customOnChange: ({ row, field, value }) => (
        Number(autoFillMovementType) === 1
          ? {
            id_handling_type: getAutoHandlingTypeId({
              ...row,
              [field]: value,
            }),
          }
          : {}
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Destination'),
      customType: 'filterLookup',
      validate: validateLookupField('id_destination'),
      field: 'id_destination',
      required: true,
      customOnChange: () => ({ id_destination_point: '0' }),
      lookup: elements,
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:DestinationPoint'),
      customType: 'filterLookup',
      validate: validateLookupField('id_destination_point'),
      field: 'id_destination_point',
      required: true,
      lookup: subElements,
      lookupFilter: (option, row) => String(option.id_element) === String(row.id_destination)
        || String(row.id_destination) === '0',
      customOnChange: ({ row, field, value }) => (
        Number(autoFillMovementType) === 1
          ? {
            id_handling_type: getAutoHandlingTypeId({
              ...row,
              [field]: value,
            }),
          }
          : {}
      ),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      customType: 'filterLookup',
      validate: validateLookupField('id_material_truck'),
      required: true,
      field: 'id_material_truck',
      lookup: materials,
      customOnChange: ({ row, field, value }) => (
        Number(autoFillMovementType) === 1
          ? {
            id_handling_type: getAutoHandlingTypeId({
              ...row,
              [field]: value,
            }),
          }
          : {}
      ),
      tooltip: true,
    },
    {
      editable: Number(autoFillMovementType) === 1 ? 'never' : 'always',
      customType: 'filterLookup',
      title: translate('common:MovementType'),
      field: 'id_handling_type',
      lookup: handlingTypes,
      lookupFilter: Number(autoFillMovementType) === 1 ? movementTypeFilter : null,
      tooltip: true,
    },
    {
      editable: 'never',
      title: translate('common:CycleStageTimes'),
      field: 'cycle_stage_times',
      render: row => (
        <IconButton
          style={{ outline: 'none' }}
          onClick={() => {
            setSelectedTransportReport(row);
            setTimeModalOpen(true);
          }}
        >
          <AccessTime style={{ color: 'rgb(242 170 0)' }} />
        </IconButton>
      ),
    },
    {
      editable: 'never',
      title: translate('common:CycleTime'),
      field: 'duration',
    },
    {
      editable: 'always',
      title: translate('common:LoadTime'),
      field: 'load_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:UnloadTime'),
      field: 'unload_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:LoadQueueTime'),
      field: 'load_queue_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:UnloadQueueTime'),
      field: 'unload_queue_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:LoadManeuverTime'),
      field: 'load_maneuver_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:UnloadManeuverTime'),
      field: 'unload_maneuver_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:EmptyTrafficTime'),
      field: 'empty_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:FullTrafficTime'),
      field: 'full_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:EmptyTrafficDistance'),
      field: 'empty_distance',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:FullTrafficDistance'),
      field: 'full_distance',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:MaintenanceStopTime'),
      field: 'code_time',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'id_operator_truck_group',
      lookup: operatorsGroups,
      required: true,
      customType: 'filterLookup',
      customOnChange: () => ({ id_operator_truck: '0' }),
      validate: validateLookupField('id_operator_truck_group'),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      customType: 'filterLookup',
      required: true,
      field: 'id_operator_truck',
      lookup: operators,
      lookupFilter: (e, f) => String(e.id_group) === String(f.id_operator_truck_group)
        || String(f.id_operator_truck_group) === '0',
      lookupKey: 'id_operator',
      validate: validateLookupField('id_operator_truck'),
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns,
      customType: 'filterLookup',
      tooltip: true,
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      required: true,
      lookup: teams,
      customType: 'filterLookup',
      validate: validateLookupField('team_id'),
      tooltip: true,
    },
    {
      editable: editLoadDefault,
      title: translate('common:LoadDefault'),
      field: 'load_manager',
      lookup: editLoadDefaultValue === 0 ? loadDefault : null,
      customType: editLoadDefaultValue === 0 ? 'filterLookup' : null,
      lookupKey: 'name',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:LoadBalance'),
      field: 'load_balance',
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:LoadEquipment'),
      required: true,
      customType: 'filterLookup',
      lookup: equipments.filter(equip => (
        [ESCAVATOR_ID, LOADER_ID, ROCK_FILL_ID].includes(equip.id_equip)
      )),
      field: 'id_load_equip',
      validate: validateLookupField('id_load_equip'),
      tooltip: true,
    },
    {
      editable: 'never',
      title: translate('common:CycleType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
    {
      editable: 'never',
      title: translate('common:DMT'),
      field: 'distance_manager',
      render: calculateDMT,
    },
    {
      editable: 'always',
      title: translate('common:SleepLevel'),
      field: 'sleep_level',
      lookup: sleepLevels,
    },
    {
      editable: 'never',
      title: translate('common:StepsPerformedByAutomation'),
      field: 'automation_toggle_is_disable',
      render: row => <RenderDistanceManagerField transportReport={row} />,
    },
    {
      editable: 'always',
      title: translate('common:OperatorLoad'),
      field: 'id_operator_load',
      lookup: operators,
      customType: 'filterLookup',
      lookupKey: 'id_operator',
      tooltip: true,
    },
    {
      editable: 'always',
      customType: 'filterLookup',
      title: translate('common:OperationType'),
      field: 'transport_operation_type',
      lookup: transportOperationTypes,
      tooltip: true,
    },
  ];

  const setChangedTransportItem = (newTransportItem) => {
    setSelectedTransportReport({ ...selectedTransportReport, ...newTransportItem });
  };

  const filterToFormFieldMap = {
    turn: 'turn',
    equipment: 'id_truck',
    equipmentType: 'id_equip',
    equipmentGroup: 'id_group',
    operatorGroup: 'id_operator_truck_group',
    operator: 'id_operator_truck',
    material: 'id_material_truck',
    startOrigin: 'id_mine',
    endOrigin: 'id_destination',
    startSubOrigin: 'id_mine_origin',
    endSubOrigin: 'id_destination_point',
    handlingType: 'id_handling_type',
  };

  const filterToFormMultiFieldMap = {
    equipmentGroup: 'id_group',
    equipment: 'id_truck',
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      if (filters[filterKey] !== undefined && filters[filterKey] !== 0) {
        acc[formField] = filters[filterKey];
      }
      return acc;
    },
    {},
  );

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValues = filters[filterKey];

      // adiciona para initialFormData somente
      // se tiver apenas um item selecionado no multiselect
      if (filterValues && filterValues.length === 1) {
        const [firstValue] = filterValues;
        acc[formField] = firstValue;
      } else {
      // se tiver mais de um ou nenhum, seta como '0'
        acc[formField] = '0';
      }
      return acc;
    },
    {},
  );

  // campos obrigatórios não devem ter valor inicial.
  const initialFormData = {
    ...columns.reduce((acc, e) => {
      if (e.required) return { ...acc, [e.field]: '' };
      return { ...acc, [e.field]: '0' };
    }, {}),
    unloaded_stop_time: 0,
    loaded_stop_time: 0,
    is_special: 0,
    id_mine_manager: 0,
    id_mine_origin_manager: 0,
  };

  // só deve conseguir salvar todas as alterações caso todos os campos
  // obrigatórios das linhas alteradas sejam preenchidos.
  const disableSaveAllChangesButton = transportList?.length ? transportList
    .filter(transport => transport.touched)
    .flatMap(transport => (
      Object.entries(transport).map(([k, v]) => (
        columns.find(col => col.field === k)?.required && v === initialFormData[k]
      ))
    ))
    .includes(true) : false;

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'transport',
    pagination: transportPagination,
    searchAction: searchTransportList,
  });

  const { page = 0, total = 0, loadingMore = false } = transportPagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        isOutsidePageNumbers={false}
        initialFormData={{
          ...initialFormData,
          ...mappedFilterData,
          ...mappedFilterMultiData,
        }}
        columns={applyDefaultSort(columns)}
        data={transportList?.map(item => ({
          ...item,
          distance_manager: calculateDMT(item),
        }))}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          search: true,
          selection: true,
          cacheKey: 'daily_parts_transport',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={newData => dispatch(createTransportItem(newData))}
        onDiscard={() => dispatch(restoreOriginalTransport())}
        onApply={(event, currentPage) => {
          const data = transportList.map(({ touched, ...t }) => {
            if (t.id_handling_type === 0 || t.id_handling_type === '0') {
              return { ...t, id_handling_type: null };
            }
            return t;
          });
          const actionData = {
            data,
            parameters: filters,
            // pagination: { page: currentPage, pageSize: 10 },
            saveLog: true,
            ogData: ogList.filter(ogl => data.some(({ id }) => id === ogl.id)),
          };
          dispatch(updateTransportList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach(({ ...row }) => {
            const newRow = checkLoadBalance(row);
            dispatch(setTransportValues({ ...newRow, touched: true }));
          });
        }}
        onDelete={(newData) => {
          newData.forEach((transport) => {
            dispatch(deleteTransportItem(transport));
          });
        }}
      />

      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="transport"
          referenceColumns={columns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>

      <Modal
        open={timeModalOpen}
        onClose={() => setTimeModalOpen(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TransportCycleTimesModal
          handleClose={() => setTimeModalOpen(false)}
          onChangeSelectedTransportReport={setChangedTransportItem}
          selectedTransportReport={selectedTransportReport}
        />
      </Modal>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:ReportUrlNotRegistered')}
        open={isReportUrlModalOpen}
        onConfirm={() => setIsReportUrlModalOpen(false)}
      />
    </>
  );
};

export default withStyles(styles)(TransportList);
